/**
 * Adaptive capture budgets.
 *
 * Guests capture on venue wifi or cellular, often many at once, so the useful question is
 * not "what is the best quality this phone can produce" but "what is the best quality that
 * will actually finish uploading before the guest gives up".
 *
 * Everything here is pure so it can be tested without a camera or a network.
 */

/** Matches ANAPI `GenerateEventMediaUploadPost::MAX_UPLOAD_BYTES`. */
export const ANAPI_MAX_IMAGE_BYTES = 8 * 1024 * 1024
/** Matches ANAPI `GenerateEventMediaUploadPost::MAX_VIDEO_BYTES`. */
export const ANAPI_MAX_VIDEO_BYTES = 250 * 1024 * 1024

/**
 * Bits per pixel per frame. 0.1 puts 1080p30 near 6.2 Mbps, which is roughly what phone
 * cameras produce for short clips and well above the 2.5 Mbps the booth used to cap at.
 */
export const BITS_PER_PIXEL = 0.1

export const MIN_VIDEO_BITRATE = 800_000
export const MAX_VIDEO_BITRATE = 12_000_000

export const NETWORK_FAST = 'fast'
export const NETWORK_MEDIUM = 'medium'
export const NETWORK_SLOW = 'slow'

const TIERS = {
  [NETWORK_FAST]: {
    photoMaxEdgePx: 2560,
    photoTargetBytes: 3 * 1024 * 1024,
    photoQuality: 0.85,
    videoWidth: 1920,
    videoHeight: 1080,
    videoBudgetBytes: 40 * 1024 * 1024
  },
  [NETWORK_MEDIUM]: {
    photoMaxEdgePx: 2048,
    photoTargetBytes: 1.5 * 1024 * 1024,
    photoQuality: 0.82,
    videoWidth: 1280,
    videoHeight: 720,
    videoBudgetBytes: 15 * 1024 * 1024
  },
  [NETWORK_SLOW]: {
    photoMaxEdgePx: 1600,
    photoTargetBytes: 800 * 1024,
    photoQuality: 0.78,
    videoWidth: 854,
    videoHeight: 480,
    videoBudgetBytes: 6 * 1024 * 1024
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

/**
 * Classify the connection. A throughput figure measured from this guest's previous upload
 * beats `navigator.connection`, which reports a coarse estimate and is absent on Safari.
 *
 * @param {{ connection?: object, measuredKbps?: number }} input
 */
export function networkTier({ connection, measuredKbps } = {}) {
  if (Number.isFinite(measuredKbps) && measuredKbps > 0) {
    if (measuredKbps >= 5000) return NETWORK_FAST
    if (measuredKbps >= 1200) return NETWORK_MEDIUM
    return NETWORK_SLOW
  }

  const effectiveType = connection?.effectiveType
  const downlink = connection?.downlink

  if (connection?.saveData === true) return NETWORK_SLOW

  if (Number.isFinite(downlink) && downlink > 0) {
    if (downlink >= 5) return NETWORK_FAST
    if (downlink >= 1.5) return NETWORK_MEDIUM
    return NETWORK_SLOW
  }

  if (effectiveType === '4g') return NETWORK_FAST
  if (effectiveType === '3g') return NETWORK_MEDIUM
  if (effectiveType === '2g' || effectiveType === 'slow-2g') return NETWORK_SLOW

  // No signal at all (Safari): assume the venue wifi is fine rather than degrading
  // everyone's capture on a guess.
  return NETWORK_FAST
}

export function tierSettings(tier) {
  return TIERS[tier] || TIERS[NETWORK_FAST]
}

/**
 * Bitrate for a frame size, before any budget clamping.
 */
export function deriveVideoBitrate({ width, height, frameRate = 30 } = {}) {
  const w = Number(width)
  const h = Number(height)
  const fps = Number(frameRate)

  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return MIN_VIDEO_BITRATE
  }

  const rate = Number.isFinite(fps) && fps > 0 ? fps : 30
  const raw = w * h * rate * BITS_PER_PIXEL
  return Math.round(clamp(raw, MIN_VIDEO_BITRATE, MAX_VIDEO_BITRATE))
}

/**
 * What to ask getUserMedia for, and what to hand MediaRecorder.
 *
 * `maxBitrate` is an app-supplied ceiling (the `max-bitrate` attribute), never a floor.
 * The projected clip size is checked against the tier budget and the hard API cap, and
 * the bitrate is reduced up front rather than discovering the clip is too big afterwards.
 *
 * @param {{ tier?: string, trackSettings?: object, maxSeconds?: number, maxBitrate?: number }} input
 */
export function videoCapturePlan({
  tier = NETWORK_FAST,
  trackSettings = null,
  maxSeconds = 20,
  maxBitrate = 0
} = {}) {
  const settings = tierSettings(tier)

  const width = Number(trackSettings?.width) || settings.videoWidth
  const height = Number(trackSettings?.height) || settings.videoHeight
  const frameRate = Number(trackSettings?.frameRate) || 30

  let bitrate = deriveVideoBitrate({ width, height, frameRate })

  if (Number.isFinite(maxBitrate) && maxBitrate > 0) {
    bitrate = Math.min(bitrate, maxBitrate)
  }

  const seconds = Number.isFinite(maxSeconds) && maxSeconds > 0 ? maxSeconds : 20
  const budgetBytes = Math.min(settings.videoBudgetBytes, ANAPI_MAX_VIDEO_BYTES)
  const projectedBytes = (bitrate / 8) * seconds

  if (projectedBytes > budgetBytes) {
    bitrate = Math.round(clamp((budgetBytes * 8) / seconds, MIN_VIDEO_BITRATE, MAX_VIDEO_BITRATE))
  }

  return {
    idealWidth: settings.videoWidth,
    idealHeight: settings.videoHeight,
    frameRate: 30,
    bitrate,
    budgetBytes,
    projectedBytes: Math.round((bitrate / 8) * seconds)
  }
}

/**
 * Photo budget, expressed as options `prepareEventImageForUpload` understands.
 *
 * `attributeMaxEdgePx` lets an app pin a smaller size (host headshots stay at 640) while
 * leaving guest capture free to use the adaptive value.
 */
export function photoCapturePlan({ tier = NETWORK_FAST, attributeMaxEdgePx = 0 } = {}) {
  const settings = tierSettings(tier)

  const maxEdgePx =
    Number.isFinite(attributeMaxEdgePx) && attributeMaxEdgePx > 0
      ? Math.min(attributeMaxEdgePx, settings.photoMaxEdgePx)
      : settings.photoMaxEdgePx

  return {
    maxEdgePx,
    targetBytes: Math.min(settings.photoTargetBytes, ANAPI_MAX_IMAGE_BYTES),
    webpQuality: settings.photoQuality,
    maxUploadBytes: ANAPI_MAX_IMAGE_BYTES
  }
}

/**
 * Throughput of a completed upload, for feeding back into the next `networkTier` call.
 */
export function measureThroughputKbps({ bytes, elapsedMs } = {}) {
  const size = Number(bytes)
  const ms = Number(elapsedMs)
  if (!Number.isFinite(size) || !Number.isFinite(ms) || size <= 0 || ms <= 0) return null
  return Math.round((size * 8) / ms)
}

/**
 * Remembered across captures within a page session, so the second contribution a guest
 * makes is sized from what their connection actually managed the first time.
 */
let observedThroughputKbps = null

export function rememberThroughput({ bytes, elapsedMs } = {}) {
  const measured = measureThroughputKbps({ bytes, elapsedMs })
  if (measured) observedThroughputKbps = measured
  return observedThroughputKbps
}

export function getObservedThroughputKbps() {
  return observedThroughputKbps
}

export function resetObservedThroughput() {
  observedThroughputKbps = null
}

/** Tier for right now, combining the browser's estimate with anything we have measured. */
export function currentNetworkTier() {
  const connection =
    typeof navigator !== 'undefined'
      ? navigator.connection || navigator.mozConnection || navigator.webkitConnection
      : null

  return networkTier({ connection, measuredKbps: observedThroughputKbps })
}
